import { PageResult, TenantMember, listTenantMembers } from '@kudos/http-client'
import { useRollbar } from '@rollbar/react'
import { useT } from '@transifex/react'
import { useEffect, useRef } from 'react'
import Tribute, { TributeItem } from 'tributejs'
import { useDebouncedCallback } from 'use-debounce'
import type { RichTextEditor } from './RichTextEditorField'
import { getFroalaMentionsStyles } from './froalaMentionsStyles'
import {
  DEBOUNCE_DELAY,
  ENTER_KEYCODE,
  MentionItem,
  TributeWithRange,
  buildMentionMenuItemHtml,
  buildMentionNoMatchHtml,
  buildMentionSearchParams,
  buildMentionSelectHtml,
  mapTenantMembersToMentionItems,
  normalizeMentionSpans,
  patchTributePositioning,
} from './froalaMentionsUtils'

export const useFroalaMentions = (
  editorInstance: RichTextEditor | null,
  tenantMemberId: string | undefined,
  isEnabled: boolean,
) => {
  const t = useT()
  const rollbar = useRollbar()
  const tributeRef = useRef<Tribute<MentionItem> | null>(null)
  const styleRef = useRef<HTMLStyleElement | null>(null)
  const isFetchingRef = useRef<boolean>(false)

  const repositionMenuIfActive = () => {
    // Reposition menu after results change to prevent losing anchor near viewport bottom
    const tribute = tributeRef.current as TributeWithRange | null
    if (tribute?.isActive) {
      tribute.range?.positionMenuAtCaret?.(false)
    }
  }

  // Inject mentions-specific CSS styles
  useEffect(() => {
    if (!isEnabled) {
      return
    }

    // Create and inject style element for mentions
    const styleId = 'froala-mentions-styles'
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      styleElement.textContent = getFroalaMentionsStyles()
      document.head.appendChild(styleElement)
      styleRef.current = styleElement
    }

    return () => {
      // Cleanup: remove style element when mentions are disabled
      if (styleRef.current && styleRef.current.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current)
        styleRef.current = null
      }
    }
  }, [isEnabled])

  // Debounced function to perform the API call
  const debouncedSearchMembers = useDebouncedCallback(
    (searchText: string, callback: (items: MentionItem[]) => void, memberId: string) => {
      if (!memberId) {
        isFetchingRef.current = false
        callback([])
        return
      }

      isFetchingRef.current = true

      const params = buildMentionSearchParams(memberId, searchText)

      listTenantMembers(params)
        .then((response: PageResult<TenantMember>) => {
          const items = mapTenantMembersToMentionItems(response?.results)
          isFetchingRef.current = false
          callback(items)
          repositionMenuIfActive()
        })
        .catch((error: unknown) => {
          rollbar.error(error instanceof Error ? error : String(error))
          isFetchingRef.current = false
          callback([])
          repositionMenuIfActive()
        })
    },
    DEBOUNCE_DELAY,
  )

  useEffect(() => {
    if (!isEnabled || !editorInstance || !tenantMemberId) {
      return
    }

    // Access the editor element directly (editor.el is the Froala editor element)
    const editor = editorInstance as unknown as {
      events: {
        on: (
          event: string,
          handler: (e: { which: number }) => boolean | void,
          capture: boolean,
        ) => void
      }
      el?: HTMLElement
    }

    if (!editor.el) {
      return
    }

    const editorElement = editor.el

    // Create Tribute instance with async lookup using listTenantMembers
    const tribute = new Tribute<MentionItem>({
      // We allow spaces so that if there are users with the same first name,
      // you can narrow down on the last name.
      allowSpaces: true,
      requireLeadingSpace: false,

      // listTenantMembers already filters. Tribute's local fuzzy filter would
      // drop matches when mentionText ends with a trailing space (e.g. user
      // typed "@John Smith" then deleted the last name, leaving "@John ").

      // Note that pre and post values are the defaults for tribute js however
      // we get a ts error if we omit them!
      searchOpts: { skip: true, pre: '<span>', post: '</span>' },
      values: function (text: string, callback: (items: MentionItem[]) => void) {
        const searchText = text.trim()

        // Set fetching flag immediately to prevent "No match found" from showing
        // while the debounced search is pending
        isFetchingRef.current = true

        // Don't call callback([]) immediately - wait for debounced search results
        // This prevents "No match found" from appearing while results are loading

        // Trigger debounced search
        debouncedSearchMembers(searchText, callback, tenantMemberId)
      },
      menuItemTemplate: item => buildMentionMenuItemHtml(item.original),
      // Cast: Tribute's typings declare the return as `string`, but its runtime
      // treats a `null` return as "insert nothing", which is how we avoid a
      // crash when the user selects with no results (see buildMentionSelectHtml).
      selectTemplate: (item => buildMentionSelectHtml(item)) as (
        item: TributeItem<MentionItem>,
      ) => string,
      noMatchTemplate: function () {
        return buildMentionNoMatchHtml(t('No results found'))
      },
    })

    patchTributePositioning(tribute)

    // Attach Tribute to editor element
    tribute.attach(editorElement)
    normalizeMentionSpans(editorElement)
    tributeRef.current = tribute

    // Guard against mention spans injected after init (e.g. draft edit via html.set).
    const mentionObserver = new MutationObserver(() => {
      normalizeMentionSpans(editorElement)
    })
    mentionObserver.observe(editorElement, { childList: true, subtree: true })

    editor.events.on(
      'keydown',
      function (e: { which: number }) {
        if (e.which === ENTER_KEYCODE && tribute.isActive) {
          return false
        }
      },
      true,
    )

    // Keep the mentions menu anchored to the "@" while any ancestor scrolls
    // or the visual viewport changes (mobile keyboard open/close, zoom).
    // This uses a lightweight repositioning path that reads the caret rect
    // directly — no marker insertion, no trigger re-detection — so it's
    // safe to run on every scroll frame without jitter.
    let scrollRafId: number | null = null
    let lastFlippedAbove = false

    const resetFlipState = () => {
      lastFlippedAbove = false
    }
    editor.el.addEventListener('tribute-active-true', resetFlipState)

    const repositionMenuOnScroll = () => {
      scrollRafId = null
      if (!tribute.isActive) return

      try {
        const sel = window.getSelection()
        if (!sel?.rangeCount) return

        const rect = sel.getRangeAt(0).getBoundingClientRect()
        if (!rect.height && !rect.top && !rect.left) return

        const vv = window.visualViewport
        const vvTop = vv?.offsetTop ?? 0
        const vvLeft = vv?.offsetLeft ?? 0
        const viewportW = vv?.width ?? window.innerWidth
        const viewportH = vv?.height ?? window.innerHeight

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const menu = (tribute as any).menu as HTMLElement
        const menuW = menu.offsetWidth
        const menuH = menu.offsetHeight

        let top = rect.bottom + vvTop
        let left = rect.left + vvLeft

        const edgePad = 8
        if (left + menuW + edgePad > vvLeft + viewportW) {
          left = Math.max(vvLeft + edgePad, vvLeft + viewportW - menuW - edgePad)
        }

        // Hysteresis: once flipped above, stay above until there's comfortable
        // room below (20 px buffer) to prevent oscillation near the edge.
        const fitsBelow = top + menuH + edgePad <= vvTop + viewportH
        const comfortablyFitsBelow = top + menuH + 20 <= vvTop + viewportH

        if (lastFlippedAbove && !comfortablyFitsBelow) {
          top = Math.max(vvTop, rect.top + vvTop - menuH)
        } else if (!fitsBelow) {
          top = Math.max(vvTop, rect.top + vvTop - menuH)
          lastFlippedAbove = true
        } else {
          lastFlippedAbove = false
        }

        menu.style.top = `${top}px`
        menu.style.left = `${left}px`
      } catch {
        // Selection may have been lost during scroll — ignore silently
      }
    }

    const handleScrollOrResize = () => {
      if (!tribute.isActive) return
      if (scrollRafId !== null) return
      scrollRafId = requestAnimationFrame(repositionMenuOnScroll)
    }

    // scroll doesn't bubble, so capture: true is needed to intercept scroll
    // events from any ancestor container (e.g. a MUI Drawer on mobile).
    document.addEventListener('scroll', handleScrollOrResize, { capture: true, passive: true })
    window.visualViewport?.addEventListener('scroll', handleScrollOrResize, { passive: true })
    window.visualViewport?.addEventListener('resize', handleScrollOrResize, { passive: true })

    return () => {
      mentionObserver.disconnect()
      document.removeEventListener('scroll', handleScrollOrResize, { capture: true })
      window.visualViewport?.removeEventListener('scroll', handleScrollOrResize)
      window.visualViewport?.removeEventListener('resize', handleScrollOrResize)
      editor.el?.removeEventListener('tribute-active-true', resetFlipState)
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)

      if (tributeRef.current && editor.el) {
        tributeRef.current.detach(editor.el)
        tributeRef.current = null
      }
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorInstance, tenantMemberId, isEnabled, debouncedSearchMembers])
}
